/* eslint-disable react/jsx-no-target-blank */

import Icon from '@/components/Icon'
import VideoModal, {
	useVideoModal,
} from '@/legacy-ported/components/base/VideoModal'
import { handleResourceDownload } from '@/legacy-ported/components/syllabus/DownloadList'
import { IPropWithClassNameChildren } from '@/types'
import { NesaSearchResult } from '@/types/searchResult'
import {
	useAppInsightsContext,
	useTrackEvent,
} from '@microsoft/applicationinsights-react-js'
import { TagList } from 'nsw-ds-react'
import { TagProps } from 'nsw-ds-react/dist/component/tags/tags'
import { MouseEvent, useEffect, useMemo, useState } from 'react'

type AzureSearchResultItemProps = {
	document: NesaSearchResult
	showTitleOnly?: boolean
}

const isWeblinkExtOrVideo = (type) =>
	['weblinkext', 'weblinkvideo'].includes(type)

type LinkCompProps = {
	href: string
	type: 'weblinkext' | 'weblinkvideo' | string
	title: string
} & IPropWithClassNameChildren

const LinkComp = ({ href, type, title, children }: LinkCompProps) => {
	const defaultClassName = 'nsw-h4 text-primary underline'

	const appInsights = useAppInsightsContext()
	const trackClick = useTrackEvent(
		appInsights,
		'Click Search Result',
		{
			url: '',
			title: '',
		},
		true,
	)
	const [isExternalUrl, setIsExternalUrl] = useState(false)
	const [isInIframe, setIsInIframe] = useState(false)
	// check if the url contains /file/
	const isFileUrl = href.includes('/file/')
	const isVideoUrl = href.includes('players.brightcove.net/')

	const {
		currentVideoIframeUrl,
		currentVideoLabel,
		openVideo,
		hideVideo,
		openVideoModal,
	} = useVideoModal()

	const handleVideoClick = (e: MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault()
		openVideo(href, title)
	}

	useEffect(() => {
		// check if the host is different
		try {
			const url = new URL(href)
			const currentUrl = new URL(window.location.href)
			setIsExternalUrl(url.host !== currentUrl.host)
		} catch (error) {
			console.error(
				`🚀 ~ AzureSearchResultItem.tsx:49 ~ useEffect ~ Invalid href: ${href}, for text: ${children}`,
			)
			setIsExternalUrl(false)
		}
		// Check if we're in an iframe
		setIsInIframe(window.parent !== window)
	}, [href, children])

	const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
		trackClick({
			url: href,
			title: (e.target as HTMLElement).innerText,
		})

		// Check if we're in an iframe and this is an internal link
		if (
			isInIframe &&
			!isExternalUrl &&
			!isFileUrl &&
			!isWeblinkExtOrVideo(type) &&
			!isVideoUrl
		) {
			// Send message to parent frame to handle navigation
			window.parent.postMessage(
				{
					type: 'routeChangeComplete',
					url: href,
				},
				'*',
			)
		}

		// Handle special content types
		if (isWeblinkExtOrVideo(type) || isVideoUrl) {
			const isExt = type === 'weblinkext'
			const isVideoContent = type === 'weblinkvideo'
			const shouldOpenVideo =
				(isVideoContent && !isFileUrl) || (isVideoUrl && !isFileUrl)
			const isDownloadable =
				!(isVideoUrl || isVideoContent) && isFileUrl && !isExt

			if (shouldOpenVideo) {
				handleVideoClick(e)
			}
			if (isDownloadable) {
				handleResourceDownload(e)
			}
		} else {
			// Handle file downloads for non-external links
			const isDownloadable = isFileUrl && !isExternalUrl
			if (isDownloadable) {
				handleResourceDownload(e)
			}
		}
	}

	if (isWeblinkExtOrVideo(type) || isVideoUrl) {
		const isExt = type === 'weblinkext'
		const isVideoContent = type === 'weblinkvideo'

		const _shouldOpenVideo =
			(isVideoContent && !isFileUrl) || (isVideoUrl && !isFileUrl)
		const isDownloadable =
			!(isVideoUrl || isVideoContent) && isFileUrl && !isExt

		return (
			<>
				<a
					href={href}
					rel={
						isExt || isFileUrl
							? 'noopener noreferrer noindex nofollow'
							: undefined
					}
					target={isExt || isFileUrl ? '_blank' : undefined}
					className={defaultClassName}
					onClick={handleClick}
					data-asset-id={isFileUrl && !isExt ? 'asset' : undefined}
				>
					{children}
					{isDownloadable && (
						<Icon
							icon={'mdi:download'}
							width={28}
							height={28}
							className="ml-1 relative bottom-[3px]"
						/>
					)}
					{(isVideoUrl || isVideoContent) && (
						<Icon
							icon={'mdi:arrow-right'}
							width={28}
							height={28}
							className="ml-1 relative bottom-[3px]"
						/>
					)}
				</a>
				{!isExt && openVideoModal && currentVideoIframeUrl && (
					<VideoModal
						key={currentVideoIframeUrl}
						ariaLabel={currentVideoLabel}
						modalStatus={openVideoModal}
						onCancel={hideVideo}
						video={currentVideoIframeUrl}
					/>
				)}
			</>
		)
	}

	const isDownloadable = isFileUrl && !isExternalUrl

	return (
		<a
			href={href}
			rel={
				isExternalUrl || isFileUrl
					? 'noopener noreferrer noindex nofollow'
					: undefined
			}
			target={isExternalUrl || isFileUrl ? '_blank' : undefined}
			className={defaultClassName}
			data-asset-id={isFileUrl && !isExternalUrl ? 'asset' : undefined}
			onClick={handleClick}
		>
			{children}
			{isDownloadable && (
				<Icon
					icon={'mdi:download'}
					width={28}
					height={28}
					className="ml-1 relative bottom-[3px]"
				/>
			)}
		</a>
	)
}

export default function AzureSearchResultItem({
	document,
	showTitleOnly = false,
}: AzureSearchResultItemProps) {
	const tags = useMemo<TagProps[]>(() => {
		if (document.content_type == 'glossary') {
			if (document.resource_category === 'assessment') {
				return [
					{
						text: 'Assessment',
					},
				]
			}
			if (document.resource_category === 'ace') {
				return document.ace_category_names?.map((name) => ({
					text: name,
				}))
			}

			return document.syllabuse_names?.map(
				(name) =>
					({
						text: name,
					} as TagProps),
			)
		}
		return []
	}, [document])

	return (
		<div key={document.content_id} className="flex flex-col gap-4 py-6">
			<div className="space-y-2">
				<LinkComp
					href={document.url || '#'}
					type={document.content_type}
					title={document.title}
				>
					{document.title}
				</LinkComp>
				{!showTitleOnly && (
					<div className="text-subtext">{document.url}</div>
				)}
			</div>
			{!!document.intro && !showTitleOnly && (
				<div
					className="text-base"
					dangerouslySetInnerHTML={{
						__html: document.intro,
					}}
				></div>
			)}
			{!!tags?.length && !showTitleOnly && (
				<TagList className="mt-2" tags={tags} />
			)}
		</div>
	)
}
